import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isoLangs } from '@zedoc/iso-langs';
import Button from '../../../common/components/Button';
import Stack from '../../../common/components/primitives/Stack';
import Cluster from '../../../common/components/primitives/Cluster';
import Text from '../../../common/components/base/Text';
import Select from '../../../components/Select';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import { notifyError, notifySuccess } from '../../../utils/notify';
import useDateTimeFormat from '../../../utils/useDateTimeFormat';
import { apiAdminUpdateUsersGroupMessageLanguages } from '../../../common/api/admin';

/**
 * TECH-2730. Which languages this organisation can author its messages in.
 *
 * WHY THIS EXISTS. Until now the answer was "whichever languages the PROVIDER
 * PORTAL INTERFACE has been translated into" — REACT_APP_LANGUAGES, a
 * build-time environment variable that CI derives from a readdir of
 * public/locales. Nothing about that list has anything to do with what
 * languages a customer's patients read. A customer with Arabic- and
 * German-speaking patients could not write to them at all, and the page gave
 * no reason.
 *
 * WHY A CONFIGURED SET RATHER THAN THE WHOLE ISO LIST. The completeness count
 * on the templates page needs a stable denominator. Deriving it live from
 * patients' preferences would move it whenever a patient is added, silently
 * flipping a message from complete to incomplete; offering all 182 languages
 * with no set at all abandons the count entirely, which is the main thing the
 * redesign is for.
 *
 * REMOVING A LANGUAGE DOES NOT DELETE ITS WORDING. The translations stay on
 * each message; they stop being offered and stop being counted. Re-adding the
 * language brings them back. The editor still shows such a language, marked,
 * so nothing is stranded where an author cannot see or delete it.
 */

const OrganisationLanguages = ({
  organisationId,
  configuredLanguages,
  patientLanguageCounts,
  patientLanguageCountsAt,
  onRefreshCounts,
  refreshingCounts,
  refreshCountsFailed,
  refreshAlreadyRunning,
  canUpdate,
}) => {
  const { t } = useTranslation();
  // A snapshot exists once it has a timestamp. Before the first resync there
  // are no numbers to show, and the panel says so rather than showing zeros:
  // a zero is also a wrong number.
  const countsComputed = !!patientLanguageCountsAt;
  const countsAsOf = useDateTimeFormat({
    date: patientLanguageCountsAt,
    options: { dateStyle: 'medium', timeStyle: 'short' },
  });
  const dispatch = useDispatch();
  const [isSaving, setSaving] = useState(false);
  const [pendingAdd, setPendingAdd] = useState(null);

  const options = useMemo(
    () =>
      Object.keys(isoLangs)
        .filter((code) => !configuredLanguages.includes(code))
        .map((code) => ({
          value: code,
          label: `${isoLangs[code].name} (${code})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [configuredLanguages],
  );

  const save = useCallback(
    (languages) => {
      setSaving(true);
      dispatch(
        callMethod(apiAdminUpdateUsersGroupMessageLanguages, {
          usersGroupId: organisationId,
          messageLanguages: languages,
        }),
      )
        .then(() => {
          notifySuccess(t('confirmations:saveEmailTemplate.success'))();
          return null;
        })
        .catch(notifyError())
        .finally(() => setSaving(false));
    },
    [dispatch, organisationId, t],
  );

  const handleAdd = useCallback(() => {
    if (!pendingAdd) {
      return;
    }
    save([...configuredLanguages, pendingAdd]);
    setPendingAdd(null);
  }, [pendingAdd, configuredLanguages, save]);

  const handleRemove = useCallback(
    (code) => save(configuredLanguages.filter((lng) => lng !== code)),
    [configuredLanguages, save],
  );

  /**
   * Languages patients prefer that nobody has configured. This is the finding
   * that made the decoupling worth doing, so the page states it rather than
   * leaving an administrator to notice.
   */
  const unservedLanguages = useMemo(
    () =>
      // Nothing to say before a snapshot exists: this is the panel's call to
      // action, and it must come from this organisation's own numbers.
      countsComputed
        ? Object.keys(patientLanguageCounts || {})
            .filter((code) => !configuredLanguages.includes(code))
            .sort()
        : [],
    [patientLanguageCounts, configuredLanguages, countsComputed],
  );

  // One label, three states, no nested ternary.
  let refreshLabel = t('refreshCounts', { defaultValue: 'Refresh' });
  if (refreshingCounts) {
    refreshLabel = t('recomputingCounts', { defaultValue: 'Recomputing…' });
  } else if (!countsComputed) {
    refreshLabel = t('computeCounts', { defaultValue: 'Compute now' });
  }

  // GATED ON organisationId, after the hooks. store.js starts with
  // organisationId: null and nothing auto-selects, so on first load this
  // panel was asserting facts about an organisation nobody had picked: an
  // empty set read as "at least one language must stay configured", and the
  // Add control was live — a click dispatched
  // apiAdminUpdateUsersGroupMessageLanguages with usersGroupId null and the
  // server answered notFound as an error toast. The accordion beside this
  // panel had the same defect and was gated in fd9f1e5547; this panel, the
  // one with the write control, was not. It already tells the user to pick an
  // organisation, so this renders nothing rather than a second prompt.
  if (!organisationId) {
    return null;
  }

  return (
    <Stack data-testid="organisation-languages">
      <Text>
        {t('organisationLanguagesHint', {
          defaultValue:
            'View and manage the languages every system message can be written in. Independent of the languages the provider portal interface is translated into.',
        })}
      </Text>

      {/* WHEN THE NUMBERS WERE TAKEN, AND THE ONLY WAY TO TAKE THEM AGAIN.
          The counts used to be computed on every page open — 546 seconds of
          database CPU on dev3 in one day, from four opens. They are now a
          snapshot on the organisation, and this row is the whole contract:
          it says when, and (for someone who can update the group) offers the
          one control that recomputes. Read-only administrators see the
          timestamp and no button. Before the first resync there is nothing
          to show, and the row says so instead of showing zeros. */}
      <Stack data-testid="patient-language-counts-status">
        <Cluster>
          <Text size="small" importance="low">
            {countsComputed
              ? t('patientLanguageCountsAsOf', {
                  when: countsAsOf,
                  defaultValue: 'Patient counts as of {{when}}',
                })
              : t('patientLanguageCountsNotComputed', {
                  defaultValue:
                    'Patient language counts have not been computed for this organisation yet.',
                })}
          </Text>
          {canUpdate && (
            <Button
              type="link"
              data-testid="refresh-patient-language-counts"
              onClick={onRefreshCounts}
              disabled={refreshingCounts}
            >
              {refreshLabel}
            </Button>
          )}
        </Cluster>
        {refreshCountsFailed && (
          // Flagged, not hidden. The previous snapshot stays with its own
          // timestamp, so nothing wrong is on screen — but the numbers the
          // administrator asked for did not arrive, and they must know.
          <Text size="small" importance="low">
            {t('patientLanguageCountsRefreshFailed', {
              defaultValue:
                'The counts could not be recomputed. The numbers shown are unchanged.',
            })}
          </Text>
        )}
        {refreshAlreadyRunning && (
          // Distinct from failure: the server refused because a recompute of
          // THIS organisation was already running — the Resync-all loop was
          // on it, or another administrator pressed first. "Unchanged" would
          // be false here; so would a promise that the numbers WILL update,
          // since that run can also fail — hence the past tense and the
          // "press again in a minute". The hook times the line out.
          <Stack data-testid="patient-language-counts-already-running">
            <Text size="small" importance="low">
              {t('patientLanguageCountsAlreadyRunning', {
                defaultValue:
                  'Another request was already recomputing these counts when you pressed Refresh. Press Refresh again in a minute to see the result.',
              })}
            </Text>
          </Stack>
        )}
      </Stack>

      {/* ONE ROW OF CHIPS, not a stacked list. Four languages previously
          occupied ~800px of vertical space — a full-width row each, with a
          pill button on the far right — pushing the message list, which is
          what the page is actually for, below the fold. A language is a short
          code and a count; it does not need a row.

          Chip shape follows the brand: mono, uppercase, 11px, 3px radius, a
          wash tint rather than a solid fill. */}
      <div className="flex flex-wrap gap-2" data-testid="configured-languages">
        {configuredLanguages.map((code) => {
          const count = (patientLanguageCounts || {})[code] || 0;
          const name = (isoLangs[code] && isoLangs[code].name) || code;
          return (
            <span
              key={code}
              // Through t(), not a template literal: this screen ships in
              // seven locales and a bare string is invisible to the i18n
              // coverage guard because it is not a t() call. Two keys rather
              // than i18next's _one/_other plural suffixes, so the choice is
              // explicit here.
              //
              // TWO SEPARATE t() CALLS, WITH THE TERNARY OUTSIDE. The first
              // version wrote t(count === 1 ? 'a' : 'b', …) and claimed in
              // this comment that "the guard can see both". It could see
              // neither: the guard matches a quote directly after t(, and a
              // ternary puts `count` there instead. Neither key was checked
              // against any locale — the exact blind spot the guard had just
              // been rebuilt to close, re-opened in the same push in a
              // different shape. A literal key per call is the only form the
              // guard sees, so it is the only form allowed here.
              title={
                // eslint-disable-next-line no-nested-ternary
                !countsComputed
                  ? name
                  : count === 1
                  ? t('patientCountOne', {
                      name,
                      count,
                      defaultValue: '{{name}} — {{count}} patient',
                    })
                  : t('patientCountOther', {
                      name,
                      count,
                      defaultValue: '{{name}} — {{count}} patients',
                    })
              }
              className="inline-flex items-center gap-2 border border-divider bg-surface-500 rounded-sm px-2 py-1"
            >
              <span className="font-mono text-xs uppercase tracking-wider">
                {code}
              </span>
              {/* TECH-2730 #4 (QA round 2/3). No visible count here: this row
                  sits above BOTH groups, so a number read as a staff count.
                  The patient counts are on the PEP group's header
                  (MessageTemplatesAccordion); the title above keeps them on
                  hover. */}
              {canUpdate && configuredLanguages.length > 1 && (
                <button
                  type="button"
                  data-testid={`remove-organisation-language-${code}`}
                  aria-label={t('removeLanguage', {
                    name,
                    defaultValue: 'Remove {{name}}',
                  })}
                  onClick={() => handleRemove(code)}
                  disabled={isSaving}
                  className="opacity-60 hover:opacity-100 leading-none px-1"
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
      </div>

      {/* The last language cannot be removed: an organisation with an empty
          set has no language rows on any message and a count of n/0, which
          reads as broken rather than as a choice. */}
      {configuredLanguages.length <= 1 && (
        <Text size="small" importance="low">
          {t('organisationLanguagesMinimum', {
            defaultValue: 'At least one language must stay configured.',
          })}
        </Text>
      )}

      {canUpdate && (
        <Cluster wrap="nowrap">
          <Select
            data-testid="add-organisation-language"
            options={options}
            value={pendingAdd}
            onChange={setPendingAdd}
            placeholder={t('addLanguage', { defaultValue: 'Add a language' })}
          />
          <Button
            data-testid="add-organisation-language-confirm"
            type="primary"
            onClick={handleAdd}
            disabled={!pendingAdd || isSaving}
          >
            {t('add')}
          </Button>
        </Cluster>
      )}

      {unservedLanguages.length > 0 && (
        <Stack data-testid="unserved-languages">
          <Text>
            {t('unservedPatientLanguages', {
              defaultValue:
                'Some patients prefer a language you have not configured. They receive each message in its default language until you add theirs.',
            })}
          </Text>
          <Cluster>
            {/* Amber TEXT on a hairline, not a filled amber block. This is a
                prompt to act, not an alarm, and the sentence above it already
                carries the meaning — colour is never the only signal.
                The `-text` variant of the warning token, never the fill one
                as text: that is 1.25:1 on white and QA could not read these
                chips (RD10). */}
            {unservedLanguages.map((code) => (
              <span
                key={code}
                className="inline-flex shrink-0 items-center rounded-sm border border-warning px-2 py-0.5 font-mono text-xs tracking-wider text-warning-text"
              >
                {`${code.toUpperCase()} ${
                  (patientLanguageCounts || {})[code] || 0
                }`}
              </span>
            ))}
          </Cluster>
        </Stack>
      )}
    </Stack>
  );
};

OrganisationLanguages.propTypes = {
  organisationId: PropTypes.string,
  configuredLanguages: PropTypes.arrayOf(PropTypes.string),
  // eslint-disable-next-line react/forbid-prop-types
  patientLanguageCounts: PropTypes.object,
  patientLanguageCountsAt: PropTypes.instanceOf(Date),
  onRefreshCounts: PropTypes.func,
  refreshingCounts: PropTypes.bool,
  refreshCountsFailed: PropTypes.bool,
  refreshAlreadyRunning: PropTypes.bool,
  canUpdate: PropTypes.bool,
};

OrganisationLanguages.defaultProps = {
  organisationId: null,
  configuredLanguages: [],
  patientLanguageCounts: {},
  patientLanguageCountsAt: null,
  onRefreshCounts: () => {},
  refreshingCounts: false,
  refreshCountsFailed: false,
  refreshAlreadyRunning: false,
  canUpdate: false,
};

export default OrganisationLanguages;
